// src/pages/AdminPage.js — Dashboard admin Takkyu Oni
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, orderBy, query, serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { QUIZ_STEPS, PERKS_LIST, ONI_FORMS, ADMIN_CATEGORIES } from "../data/demons";
import "./AdminPage.css";

const ADMIN_EMAILS = ["nayth@takkyuoni.com","aino@takkyuoni.com"];
const checkAdmin = (user) => user && ADMIN_EMAILS.includes(user.email);

// ── Tabs ──────────────────────────────────────────────────────────────────
const TABS = [
  { id:"kanban",  label:"🖼 Kanban Assets" },
  { id:"quiz",    label:"📝 Éditeur Quiz" },
  { id:"oni",     label:"👹 Éditeur Oni" },
  { id:"users",   label:"👤 Utilisateurs" },
];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("kanban");

  useEffect(() => {
    if (!loading && user !== undefined && !checkAdmin(user)) navigate("/");
  }, [user, loading, navigate]);

  if (loading || user === undefined) return <div className="admin-loading"><span className="spinner"/></div>;
  if (!checkAdmin(user)) return null;

  return (
    <div className="admin-page">
      <div className="admin-sidebar">
        <div className="admin-logo">
          <span>👹</span>
          <div>
            <p className="admin-brand">TAKKYU ONI</p>
            <p className="admin-role">ADMIN PANEL</p>
          </div>
        </div>
        <nav className="admin-nav">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`admin-nav-btn${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="admin-user-info">
          <p>{user?.email}</p>
          <p>Connecté en tant qu'admin</p>
        </div>
      </div>

      <div className="admin-content">
        {tab === "kanban" && <KanbanTab />}
        {tab === "quiz"   && <QuizEditorTab />}
        {tab === "oni"    && <OniEditorTab />}
        {tab === "users"  && <UsersTab />}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// KANBAN ASSETS
// ══════════════════════════════════════════════════════════════════════════
function KanbanTab() {
  const [assets, setAssets]       = useState([]);
  const [activeCategory, setActive] = useState("logo");
  const [uploading, setUploading] = useState(false);
  const [modal, setModal]         = useState(null); // asset en détail
  const fileRef = useRef();

  useEffect(() => {
    const q = query(collection(db, "admin_assets"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => {
      setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const byCategory = ADMIN_CATEGORIES.map(cat => ({
    ...cat,
    assets: assets.filter(a => a.category === cat.id),
  }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const storageRef = ref(storage, `admin_assets/${Date.now()}_${file.name}`);
        const snap = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snap.ref);
        await addDoc(collection(db, "admin_assets"), {
          url, fileName: file.name, category: activeCategory,
          status: "candidate", rating: 0, comments: [],
          uploadedBy: "admin", createdAt: serverTimestamp(),
        });
      }
    } catch(e) { console.error(e); }
    setUploading(false);
    fileRef.current.value = "";
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "admin_assets", id), { status });
  };

  const updateRating = async (id, rating) => {
    await updateDoc(doc(db, "admin_assets", id), { rating });
  };

  const addComment = async (id, text, existingComments) => {
    if (!text.trim()) return;
    await updateDoc(doc(db, "admin_assets", id), {
      comments: [...(existingComments||[]), { text, author:"admin", date: new Date().toISOString() }]
    });
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Supprimer cet asset ?")) return;
    await deleteDoc(doc(db, "admin_assets", id));
    setModal(null);
  };

  const STATUS_COLORS = {
    candidate: "var(--ash)",
    approved:  "var(--ember)",
    validated: "#4a9e6b",
    rejected:  "var(--blood)",
  };

  return (
    <div className="kanban-tab">
      <div className="admin-section-header">
        <div>
          <h2>Kanban Assets</h2>
          <p>Gestion des graphismes candidats par catégorie</p>
        </div>
        <div style={{display:"flex", gap:"8px", alignItems:"center"}}>
          <select
            value={activeCategory}
            onChange={e => setActive(e.target.value)}
            className="admin-select"
          >
            {ADMIN_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? <><span className="spinner"/> Upload...</> : "+ UPLOADER"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={handleUpload}/>
        </div>
      </div>

      {/* Category tabs */}
      <div className="kanban-cats">
        {byCategory.map(cat => (
          <button
            key={cat.id}
            className={`kanban-cat-btn${activeCategory === cat.id ? " active" : ""}`}
            onClick={() => setActive(cat.id)}
          >
            {cat.label}
            <span className="kanban-cat-count">{cat.assets.length}</span>
          </button>
        ))}
      </div>

      {/* Kanban columns */}
      <div className="kanban-board">
        {["candidate","approved","validated","rejected"].map(status => {
          const col = byCategory.find(c => c.id === activeCategory)?.assets.filter(a => a.status === status) || [];
          const labels = { candidate:"CANDIDAT", approved:"APPROUVÉ", validated:"VALIDÉ", rejected:"REJETÉ" };
          return (
            <div key={status} className="kanban-col">
              <div className="kanban-col-header" style={{borderTopColor: STATUS_COLORS[status]}}>
                <span style={{color: STATUS_COLORS[status]}}>{labels[status]}</span>
                <span className="kanban-col-count">{col.length}</span>
              </div>
              <div className="kanban-col-body">
                {col.map(asset => (
                  <div key={asset.id} className="kanban-card" onClick={() => setModal(asset)}>
                    <div className="kanban-thumb">
                      <img src={asset.url} alt={asset.fileName} />
                    </div>
                    <div className="kanban-card-info">
                      <p className="kanban-file-name">{asset.fileName}</p>
                      <div className="kanban-stars">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} className={`star-btn${(asset.rating||0) >= n ? " lit" : ""}`}
                            onClick={e => { e.stopPropagation(); updateRating(asset.id, n); }}>★</button>
                        ))}
                      </div>
                      {asset.comments?.length > 0 && (
                        <p className="kanban-comment-count">💬 {asset.comments.length} commentaire{asset.comments.length>1?"s":""}</p>
                      )}
                    </div>
                  </div>
                ))}
                {col.length === 0 && (
                  <div className="kanban-empty">Aucun asset</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Asset modal */}
      {modal && (
        <AssetModal
          asset={modal}
          onClose={() => setModal(null)}
          onStatusChange={(status) => { updateStatus(modal.id, status); setModal({...modal, status}); }}
          onRatingChange={(r) => { updateRating(modal.id, r); setModal({...modal, rating:r}); }}
          onComment={(text) => { addComment(modal.id, text, modal.comments); setModal({...modal, comments:[...(modal.comments||[]), {text,author:"admin",date:new Date().toISOString()}]}); }}
          onDelete={() => deleteAsset(modal.id)}
          statusColors={STATUS_COLORS}
        />
      )}
    </div>
  );
}

function AssetModal({ asset, onClose, onStatusChange, onRatingChange, onComment, onDelete, statusColors }) {
  const [commentText, setCommentText] = useState("");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <p className="modal-filename">{asset.fileName}</p>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-image">
            <img src={asset.url} alt={asset.fileName}/>
          </div>
          <div className="modal-controls">
            {/* Status */}
            <div className="modal-section">
              <p className="modal-label">STATUT</p>
              <div className="modal-status-btns">
                {["candidate","approved","validated","rejected"].map(s => (
                  <button
                    key={s}
                    className={`modal-status-btn${asset.status === s ? " active" : ""}`}
                    style={asset.status === s ? {borderColor: statusColors[s], color: statusColors[s]} : {}}
                    onClick={() => onStatusChange(s)}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {/* Rating */}
            <div className="modal-section">
              <p className="modal-label">NOTE</p>
              <div className="modal-stars">
                {[1,2,3,4,5].map(n => (
                  <button key={n} className={`modal-star${(asset.rating||0) >= n ? " lit" : ""}`}
                    onClick={() => onRatingChange(n)}>★</button>
                ))}
              </div>
            </div>
            {/* Comments */}
            <div className="modal-section modal-comments">
              <p className="modal-label">COMMENTAIRES</p>
              <div className="modal-comments-list">
                {(asset.comments||[]).map((c,i) => (
                  <div key={i} className="modal-comment">
                    <span className="comment-author">{c.author}</span>
                    <p className="comment-text">{c.text}</p>
                    <span className="comment-date">{new Date(c.date).toLocaleDateString("fr-FR")}</span>
                  </div>
                ))}
                {!asset.comments?.length && <p className="modal-no-comment">Aucun commentaire</p>}
              </div>
              <div className="modal-comment-form">
                <textarea
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  rows={2}
                />
                <button className="btn btn-primary btn-sm" onClick={() => { onComment(commentText); setCommentText(""); }}>
                  ENVOYER
                </button>
              </div>
            </div>
            {/* Actions */}
            <div className="modal-section">
              <a href={asset.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{width:"100%", justifyContent:"center", marginBottom:"6px"}}>
                OUVRIR L'IMAGE ↗
              </a>
              <button className="btn-delete" onClick={onDelete}>SUPPRIMER</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// QUIZ EDITOR
// ══════════════════════════════════════════════════════════════════════════
function QuizEditorTab() {
  const [steps, setSteps] = useState(QUIZ_STEPS);
  const [saved, setSaved]   = useState(false);

  const updateOption = (stepIdx, optIdx, field, value) => {
    setSteps(prev => {
      const next = prev.map(s => ({...s, options:[...s.options]}));
      next[stepIdx].options[optIdx] = { ...next[stepIdx].options[optIdx], [field]: value };
      return next;
    });
  };

  const updateQuestion = (stepIdx, field, value) => {
    setSteps(prev => {
      const next = [...prev];
      next[stepIdx] = { ...next[stepIdx], [field]: value };
      return next;
    });
  };

  const saveToFirestore = async () => {
    await updateDoc(doc(db, "admin_config", "quiz"), { steps, updatedAt: serverTimestamp() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="editor-tab">
      <div className="admin-section-header">
        <div>
          <h2>Éditeur de Quiz</h2>
          <p>Modifie les questions, options et sous-titres du quiz</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={saveToFirestore}>
          {saved ? "✓ SAUVEGARDÉ" : "SAUVEGARDER"}
        </button>
      </div>

      {steps.map((step, si) => (
        <div key={step.id} className="editor-step">
          <div className="editor-step-header">
            <span className="editor-step-num">ÉTAPE {si+1}</span>
            <span className="editor-step-id">{step.id}</span>
          </div>
          <div className="editor-field">
            <label>QUESTION</label>
            <input type="text" value={step.question}
              onChange={e => updateQuestion(si, "question", e.target.value)}/>
          </div>
          <div className="editor-field">
            <label>SOUS-TITRE</label>
            <input type="text" value={step.subtitle}
              onChange={e => updateQuestion(si, "subtitle", e.target.value)}/>
          </div>
          <div className="editor-options">
            {step.options.map((opt, oi) => (
              <div key={opt.value} className="editor-option">
                <div className="editor-option-icon">{opt.icon}</div>
                <div className="editor-option-fields">
                  <div className="editor-field">
                    <label>LABEL</label>
                    <input type="text" value={opt.label}
                      onChange={e => updateOption(si, oi, "label", e.target.value)}/>
                  </div>
                  <div className="editor-field">
                    <label>DESCRIPTION</label>
                    <textarea value={opt.desc} rows={2}
                      onChange={e => updateOption(si, oi, "desc", e.target.value)}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ONI EDITOR
// ══════════════════════════════════════════════════════════════════════════
function OniEditorTab() {
  const [onis, setOnis]   = useState(ONI_FORMS);
  const [active, setActive] = useState(0);
  const [saved, setSaved]   = useState(false);

  const oni = onis[active];

  const update = (field, value) => {
    setOnis(prev => {
      const next = [...prev];
      next[active] = { ...next[active], [field]: value };
      return next;
    });
  };

  const save = async () => {
    await updateDoc(doc(db, "admin_config", "oni_forms"), { forms: onis, updatedAt: serverTimestamp() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="editor-tab">
      <div className="admin-section-header">
        <div>
          <h2>Éditeur Oni</h2>
          <p>Modifie les descriptions, stats et propriétés de chaque Oni</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={save}>
          {saved ? "✓ SAUVEGARDÉ" : "SAUVEGARDER"}
        </button>
      </div>

      <div className="oni-editor-layout">
        {/* Oni selector */}
        <div className="oni-selector">
          {onis.map((o, i) => (
            <button key={o.id}
              className={`oni-sel-btn${active === i ? " active" : ""}`}
              onClick={() => setActive(i)}
            >
              <span>{o.emoji}</span>
              <span>{o.name}</span>
            </button>
          ))}
        </div>

        {/* Oni editor */}
        <div className="oni-editor-fields">
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px"}}>
            <div className="editor-field">
              <label>EMOJI</label>
              <input type="text" value={oni.emoji} onChange={e => update("emoji", e.target.value)}/>
            </div>
            <div className="editor-field">
              <label>NOM JP</label>
              <input type="text" value={oni.jp} onChange={e => update("jp", e.target.value)}/>
            </div>
            <div className="editor-field">
              <label>NOM EN</label>
              <input type="text" value={oni.name} onChange={e => update("name", e.target.value)}/>
            </div>
            <div className="editor-field">
              <label>SIGNE {oni.sign}</label>
              <input type="text" value={oni.signName} onChange={e => update("signName", e.target.value)}/>
            </div>
          </div>
          <div className="editor-field">
            <label>TAGLINE</label>
            <input type="text" value={oni.tagline} onChange={e => update("tagline", e.target.value)}/>
          </div>
          <div className="editor-field">
            <label>DESCRIPTION LORE</label>
            <textarea value={oni.desc} rows={4} onChange={e => update("desc", e.target.value)}/>
          </div>
          {/* Stats */}
          <div className="editor-stats-grid">
            <p className="modal-label" style={{gridColumn:"1/-1"}}>STATISTIQUES</p>
            {Object.entries(oni.stats).map(([key, val]) => (
              <div key={key} className="editor-stat">
                <label>{key.toUpperCase()}</label>
                <input type="range" min="0" max="99" value={val}
                  onChange={e => update("stats", {...oni.stats, [key]: Number(e.target.value)})}/>
                <span>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// USERS TAB
// ══════════════════════════════════════════════════════════════════════════
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  return (
    <div className="editor-tab">
      <div className="admin-section-header">
        <div>
          <h2>Utilisateurs</h2>
          <p>{users.length} compte{users.length > 1 ? "s" : ""} enregistré{users.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      {loading ? <div style={{textAlign:"center", padding:"3rem"}}><span className="spinner"/></div> : (
        <div className="users-table">
          <div className="users-thead">
            <span>UTILISATEUR</span><span>DÉMON</span><span>LEVEL</span><span>XP</span><span>BADGE</span><span>JOURNAL</span>
          </div>
          {users.map(u => (
            <div key={u.id} className="users-row">
              <div className="user-identity">
                <p className="user-name">{u.displayName || "—"}</p>
                <p className="user-email">{u.email}</p>
              </div>
              <div>
                {u.demon?.oni ? (
                  <span className="badge badge-blood">{u.demon.oni.emoji} {u.demon.oni.name}</span>
                ) : <span className="badge badge-ghost">—</span>}
              </div>
              <div>
                {u.xp ? (
                  <span className="badge badge-ember">LV.{(()=>{
                    const thresholds=[0,100,250,500,900,1500,2500,4000,6000,9999];
                    let l=1; thresholds.forEach((t,i)=>{ if(u.xp>=t) l=i+1; }); return Math.min(l,10);
                  })()}</span>
                ) : <span>—</span>}
              </div>
              <div><span style={{fontFamily:"var(--font-mono)", fontSize:"0.75rem"}}>{u.xp || 0}</span></div>
              <div><span style={{fontSize:"0.7rem", color:"var(--ash)"}}>{u.badges?.length || 0} badges</span></div>
              <div><span style={{fontSize:"0.7rem", color:"var(--ash)"}}>{u.journal?.length || 0} entrées</span></div>
            </div>
          ))}
          {users.length === 0 && <div className="kanban-empty" style={{gridColumn:"1/-1"}}>Aucun utilisateur</div>}
        </div>
      )}
    </div>
  );
}
